/**
 * ViewWorkOrderWrapper
 *
 * Purpose:
 * - Wraps WO view page to route back into vendor orders using returnTab.
 */
import React, { useEffect, useState } from "react";
import { connect, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import { CONTRACTORS, WORK_ORDER, VENDOR, VENDOR_ORDERS, VENDOR_PAYMENT, VENDOR_INVOICES } from "../../constants/route-paths";
import {
  getPurchaseOrder,
  deletePurchaseOrder,
  downloadProjectPO,
  cancelWorkOrder,
  reactivateWorkOrder,
} from "../../actions/project-management.action";
import { getGlobalSettingTemplateTypes } from "../../actions/global-setting-template.action";
import idx from "idx";
import { numberFormat } from "../../util/currency-formatter";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import Breadcrumb from 'react-bootstrap/Breadcrumb';
import Table from 'react-bootstrap/Table';
import { Button, Popover, OverlayTrigger } from "react-bootstrap";
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { VENDOR_TAB, VENDOR_URL_PARAM, VENDOR_TYPE } from "../../constants/vendor.contant";

import WorkOrderInvoicesSection from "../../components/work-order-invoices-section";
import DownloadDialog from "../../components/download-dialog";
import DeleteDialog from "../../components/delete-dialog";
import Modal from "../../components/modal/modal";
import PayInvoice from "../vendor-payment/pay-invoice";

function ViewWorkOrderWrapper({
  getPurchaseOrder,
  deletePurchaseOrder,
  downloadProjectPO,
  cancelWorkOrder,
  reactivateWorkOrder,
  getGlobalSettingTemplateTypes,
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const params = useParams();

  const returnTab = searchParams.get(VENDOR_URL_PARAM.RETURN_TAB) || VENDOR_TAB.WORK_ORDERS;
  const returnSubTab = searchParams.get(VENDOR_URL_PARAM.RETURN_SUB_TAB);
  const [PO, setPO] = useState({});
  const [total, setTotal] = useState(0);

  const [downloadData, setDownloadData] = useState({ open: false, id: "" });
  const [templateOptions, setTemplateOptions] = useState([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [openReactivateDialog, setOpenReactivateDialog] = useState(false);
  const [showPayInvoice, setShowInvoice] = useState(false);

  const userAccessControl = useSelector((state) => state.user?.userAccessControl);
  const writePermission = userAccessControl === 2 || userAccessControl === undefined;

  useEffect(() => {
    getPO();
  }, []);

  const getPO = async () => {
    const poId = params.poId;
    const query = `?poId=${poId}`;
    let res = await getPurchaseOrder(query);
    setPO(res);
    let totalCost = 0;

    Array.isArray(res.items) &&
      res.items.forEach(({ total }) => (totalCost += total));
    setTotal(totalCost);
  };

  const cancelHandler = () => {
    if (returnTab) {
      // Return to vendor orders with the correct tab and sub-tab
      let url = `/${VENDOR}/${VENDOR_ORDERS}?${VENDOR_URL_PARAM.TAB}=${returnTab}`;
      if (returnSubTab) {
        url += `&${VENDOR_URL_PARAM.SUB_TAB}=${returnSubTab}`;
      }
      navigate(url);
    } else {
      // Default to original contractor path
      navigate(`/${CONTRACTORS}/${WORK_ORDER}`);
    }
  };

  const gettemplate = async (type) => {
    try {
      let newdata = [];
      const templateData = await getGlobalSettingTemplateTypes(type, null);
      if (Array.isArray(templateData)) {
        templateData.forEach((item) => {
          newdata.push({
            value: item.id,
            key: item.name,
          });
        });
      }
      setTemplateOptions(newdata);
    } catch (e) {
      console.error("Failed to fetch templates:", e);
    }
  };

  const handleDownloadDialog = (id = "", templateType = "work_order") => {
    const actualId = id || params.poId;
    setDownloadData((prev) => {
      const isOpening = !prev.open;
      const newDownloadData = { ...prev, open: isOpening, id: actualId || "" };
      if (isOpening) {
        gettemplate(templateType);
      }
      return newDownloadData;
    });
  };

  const downloadFile = async (fileFormat = "pdf", tempId) => {
    try {
      await downloadProjectPO(downloadData.id, fileFormat, tempId);
      handleDownloadDialog();
    } catch (e) {
      console.error("Download failed:", e);
    }
  };

  const deleteDialogState = () => {
    setOpenDeleteDialog((prev) => !prev);
  };

  const deleteHandler = async () => {
    deleteDialogState();
    if (params.poId) {
      await deletePurchaseOrder(params.poId, "Work Order Deleted successfully");
      cancelHandler();
    }
  };

  const cancelDialogState = () => {
    setOpenCancelDialog((prev) => !prev);
  };

  const confirmCancelWO = async () => {
    if (params.poId) {
      const res = await cancelWorkOrder(params.poId);
      if (res?.status === "SUCCESS" || res?.message?.includes("success")) {
        cancelDialogState();
        getPO();
      }
    }
  };

  const reactivateDialogState = () => {
    setOpenReactivateDialog((prev) => !prev);
  };

  const confirmReactivateWO = async () => {
    if (params.poId) {
      const res = await reactivateWorkOrder(params.poId);
      if (res?.status === "SUCCESS" || res?.message?.includes("success")) {
        reactivateDialogState();
        getPO();
      }
    }
  };

  const editOrder = () => {
    navigate(
      `/${VENDOR}/${VENDOR_ORDERS}/work-order/edit/${params.poId}/project?returnTab=${returnTab}&${VENDOR_URL_PARAM.SUB_TAB}=${returnSubTab}`
    );
  };

  const handlePayInvoiceModal = (state) => {
    setShowInvoice(state);
    if (!state) {
      getPO();
    }
  };

  const viewInvoices = () => {
    if (returnTab) {
      navigate(`/${VENDOR}/${VENDOR_INVOICES}?tab=contractor-invoices&poId=${params.poId}&type=project`);
    } else {
      const projId = PO.projectId || idx(PO, (_) => _.project.id);
      navigate(`/${VENDOR_PAYMENT}?project=${projId}&woId=${params.poId}`);
    }
  };

  const closeMenuAnd = (callback) => {
    document.body.click();
    if (callback) callback();
  };

  const renderMenu = (
    <Popover id="popover-basic" data-cy="wo-view-action-menu">
      <Popover.Body>
        <ul className="action-block-list mb-0">
          <li onClick={() => closeMenuAnd(() => handleDownloadDialog(params.poId, "work_order"))} data-cy="download-wo">
            Download WO
          </li>
          {writePermission && PO.status !== "Completed" && PO.status !== "Finished" && (
            <li onClick={() => closeMenuAnd(editOrder)} data-cy="edit-wo">
              Edit WO
            </li>
          )}
          {writePermission && (
            <li onClick={() => closeMenuAnd(() => handlePayInvoiceModal(true))} data-cy="receive-invoice">
              Receive Invoice
            </li>
          )}
          {writePermission && (
            <li onClick={() => closeMenuAnd(viewInvoices)} data-cy="view-invoices">
              View Invoices
            </li>
          )}
          {writePermission &&
            (PO.status === "Approved" || PO.status === "Active" || PO.status === "Pending") &&
            (PO.invoicestatus === "Not Invoiced" ? (
              <li onClick={() => closeMenuAnd(cancelDialogState)} data-cy="cancel-wo">
                Cancel
              </li>
            ) : PO.invoicestatus === "Partially Invoiced" || PO.invoicestatus === "Fully Invoiced" ? (
              <li className="disabled" data-cy="cancel-wo-disabled">
                <span title="Cannot cancel a partially or fully invoiced Work Order">Cancel</span>
              </li>
            ) : (
              <li onClick={() => closeMenuAnd(cancelDialogState)} data-cy="cancel-wo">
                Cancel
              </li>
            ))}
          {writePermission &&
            (PO.status === "Pending" || (PO.status === "Approved" && PO.invoicestatus === "Not Invoiced")) && (
              <li onClick={() => closeMenuAnd(deleteDialogState)} data-cy="delete-wo">
                Delete
              </li>
            )}
          {writePermission && PO.status === "Cancelled" && (
            <li onClick={() => closeMenuAnd(reactivateDialogState)} data-cy="reactivate-wo">
              Reactivate
            </li>
          )}
        </ul>
      </Popover.Body>
    </Popover>
  );

  return (
    <div className="purchase-order-block" data-cy="wo-details">
      <DownloadDialog
        open={downloadData.open}
        handleDownloadDialog={handleDownloadDialog}
        downloadFile={downloadFile}
        templateOptions={templateOptions}
        downloadModuleName="Work Order"
        dataCy="download-wo-dialog"
      />
      <DeleteDialog
        open={openDeleteDialog}
        id={params.poId}
        deleteHandler={deleteHandler}
        dialogClose={deleteDialogState}
        moduleName="Work Order"
        description={`Work Order - ${PO.f_id || PO.id}`}
        dataCy="delete-wo-dialog"
      />
      <Modal
        open={openCancelDialog}
        onClose={cancelDialogState}
        fullWidth={true}
        maxWidth="sm"
        modalHeading="Cancel Work Order"
        modalSaveTxt={<span data-cy="confirm-cancel">Yes</span>}
        modalCloseTxt="No"
        onClick={confirmCancelWO}
        showActionBtn
        savebtnClass="createBtn"
        cxlbtnClass="cancelBtn"
        primary
        dataCy="cancel-wo-dialog"
      >
        <p>Are you sure you want to cancel Work Order - {PO.f_id || PO.id}? </p>
      </Modal>
      <Modal
        open={openReactivateDialog}
        onClose={reactivateDialogState}
        fullWidth={true}
        maxWidth="sm"
        modalHeading="Reactivate Work Order"
        modalSaveTxt={<span data-cy="confirm-reactivate">Yes</span>}
        modalCloseTxt="No"
        onClick={confirmReactivateWO}
        showActionBtn
        savebtnClass="createBtn"
        cxlbtnClass="cancelBtn"
        primary
        dataCy="reactivate-wo-dialog"
      >
        <p>Are you sure you want to reactivate Work Order - {PO.f_id || PO.id}? </p>
      </Modal>
      {showPayInvoice && (
        <PayInvoice
          open={showPayInvoice}
          handleClose={() => handlePayInvoiceModal(false)}
          projectId={PO.projectId || idx(PO, (_) => _.project.id)}
          vendorId={PO.vendorId || idx(PO, (_) => _.vendor.id)}
          poId={params.poId}
          poNo={PO.f_id || PO.id}
          projectName={idx(PO, (_) => _.project.projectName) || idx(PO, (_) => _.projectName)}
          vendorName={idx(PO, (_) => _.vendor.business_name) || idx(PO, (_) => _.vendorName)}
          initialVendorType={idx(PO, (_) => _.vendor.type) || VENDOR_TYPE.SUB_CONTRACTOR}
          isSubContractor={true}
          workType="project"
          payInvoiceType="workOrder"
          handleDownloadDialog={handleDownloadDialog}
          getWOList={getPO}
          dataCy="receive-invoice-wo-modal"
        />
      )}

      <div className="d-flex align-items-center justify-content-between">
        <h2 className="content-heading text-uppercase mb-0" data-cy="heading">
          View WO
        </h2>
        <div className="action-dropdown me-4">
          <OverlayTrigger trigger="click" rootClose placement="bottom" overlay={renderMenu}>
            <Button variant="default" data-cy="action-menu-button">
              <MoreHorizIcon />
            </Button>
          </OverlayTrigger>
        </div>
      </div>

      <div className="col-12 breadcrumb-block p-0" data-cy="breadcrumb">
        <Breadcrumb>
          <Breadcrumb.Item onClick={cancelHandler} data-cy="breadcrumb-orders">
            {returnTab ? "Orders" : "Work Order"}
          </Breadcrumb.Item>
          <Breadcrumb.Item active data-cy="breadcrumb-view-wo">View WO</Breadcrumb.Item>
        </Breadcrumb>
      </div>

      <div className="row">
        <div className="col-lg-4 mt-3">
          <div className="view-block" data-cy="wo-number">
            <label>WO Number</label>
            {/* f_id is the WO number with project prefix and Suffix if null then default to wo Number*/}
            <p>{PO.f_id || PO.id}</p>
          </div>
        </div>
        <div className="col-lg-4 mt-3">
          <div className="view-block" data-cy="vendor-type">
            <label>Vendor Type</label>
            <p>Contractor</p>
          </div>
        </div>
        <div className="col-lg-4 mt-3">
          <div className="view-block" data-cy="vendor-name">
            <label>Vendor Name</label>
            <p>{idx(PO, (_) => _.vendor.business_name) || "-"}</p>
          </div>
        </div>
        <div className="col-lg-4 mt-3">
          <div className="view-block" data-cy="notes">
            <label>Notes</label>
            <p>{idx(PO, (_) => _.notes) || "-"}</p>
          </div>
        </div>
        <div className="col-lg-4 mt-3">
          <div className="view-block" data-cy="street-address">
            <label>Street Address</label>
            <p>{idx(PO, (_) => _.street_address) || "-"}</p>
          </div>
        </div>
        <div className="col-lg-4 mt-3">
          <div className="view-block" data-cy="suburb">
            <label>Suburb</label>
            <p>{idx(PO, (_) => _.suburb) || "-"}</p>
          </div>
        </div>
        <div className="col-lg-4 mt-3">
          <div className="view-block" data-cy="state">
            <label>State</label>
            <p>{idx(PO, (_) => _.state) || "-"}</p>
          </div>
        </div>
        <div className="col-lg-4 mt-3">
          <div className="view-block" data-cy="postcode">
            <label>Postcode</label>
            <p>{idx(PO, (_) => _.postcode) || "-"}</p>
          </div>
        </div>
        <div className="col-lg-4 mt-3">
          <div className="view-block" data-cy="country">
            <label>Country</label>
            <p>{idx(PO, (_) => _.country) || "-"}</p>
          </div>
        </div>
        <div className="col-lg-4 mt-3">
          <div className="view-block" data-cy="total">
            <label>Total</label>
            <p>{numberFormat(total)}</p>
          </div>
        </div>
      </div>

      <div className="material-list-block mt-3" data-cy="material-list">
        <div className="table-responsive">
          <Table bordered className="table-create table-material table-material-ch">
            <thead>
              <tr>
                <th className="w-auto" data-cy="name">Name</th>
                <th className="w-auto" data-cy="type">Type</th>
                <th className="text-right" data-cy="quotation">Quotation</th>
              </tr>
            </thead>
            <tbody data-cy="material-list-body">
              {(PO.items || []).map((item, index) => {
                return (
                  <tr key={item.id} data-cy="material-list-item">
                    <td data-cy="name">{item.taskName}</td>
                    <td data-cy="type">{item.taskDescription}</td>
                    <td className="text-right" data-cy="quotation">{numberFormat(item.total)}</td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        </div>
      </div>

      <WorkOrderInvoicesSection
        poUuid={params.poId}
        projectId={PO.projectId}
        vendorType="sub-contractor"
        onNavigate={(invoiceId) => navigate(`/vendor/invoices/contractor/view/${invoiceId}?id=${PO.projectId}&poId=${params.poId}&type=contractor`)}
      />

      <div className="col-12 d-flex mt-3 justify-content-end">
        <Button type="submit" onClick={cancelHandler} className="secondarybtn cancelbtn" data-cy="cancel-button">BACK</Button>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({});

const mapDispatchToProps = (dispatch) => ({
  getPurchaseOrder: bindActionCreators(getPurchaseOrder, dispatch),
  deletePurchaseOrder: bindActionCreators(deletePurchaseOrder, dispatch),
  downloadProjectPO: bindActionCreators(downloadProjectPO, dispatch),
  cancelWorkOrder: bindActionCreators(cancelWorkOrder, dispatch),
  reactivateWorkOrder: bindActionCreators(reactivateWorkOrder, dispatch),
  getGlobalSettingTemplateTypes: bindActionCreators(getGlobalSettingTemplateTypes, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(ViewWorkOrderWrapper);
